import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { QuicklinkStrategy } from 'ngx-quicklink';

import { AdminProhibitedGuard, AuthGuard, LeadAdminGuard, NoAuthGuard, OwnerEntityGuard, OwnerGuard, SecondaryOwnerProhibitedGuard } from '@app/core';
import { BlankLayoutComponent, DemoLayoutComponent, HomeLayoutComponent, LoginLayoutComponent } from '@app/layout';
import { CanViewReportGuard } from './core/guards/can-view-report.guard';
import { CommonSubscriptionGuard } from './core/guards/common-subscription.guard';
import { AuthCallbackComponent } from './modules/auth/components/auth-callback/auth-callback.component';
import { LogoutComponent } from './modules/auth/components/logout/logout.component';
import { HyperpureCartIframeComponent } from './modules/purchase-manager/components/hyperpure-cart-iframe/hyperpure-cart-iframe.component';
import { PmLandingComponent } from './modules/purchase-manager/components/pm-landing/pm-landing.component';
import { PmPriceDiscoveryComponent } from './modules/purchase-manager/components/pm-price-discovery/pm-price-discovery.component';
import { NotFoundComponent } from './shared';
import { InvoiceExtractionComponent } from './shared/invoice-extraction/invoice-extraction.component';

const routes: Routes = [
  {
    path: 'auth/callback',
    component: AuthCallbackComponent
  },
  {
    path: 'logout',
    component: LogoutComponent
  },
  {
    path: 'login',
    component: LoginLayoutComponent,
    canActivate: [NoAuthGuard],
    children: [
      {
        path: '',
        loadChildren: () => import('@modules/auth/auth.module').then(m => m.AuthModule)
      },
      // { 
      //   path: 'auth/callback',
      //   component: AuthCallbackComponent
      // }
    ]
  },
  {
    path: 'purchase-manager-demo',
    component: DemoLayoutComponent,
    children: [
      {
        path: '',
        component: PmLandingComponent,
        data: { title: 'Purchase Manager', breadcrumb: 'Purchase Manager' }
      },
      {
        path: 'price-discovery',
        component: PmPriceDiscoveryComponent,
        data: { title: 'Price Discovery', breadcrumb: 'Price Discovery' }
      }
    ]
  },
  {
    path: 'purchase-manager/hyperpure-cart',
    component: HyperpureCartIframeComponent,
    data: { title: 'Hyperpure Cart', breadcrumb: 'Hyperpure Cart' }
  },
  {
    path: '',
    component: HomeLayoutComponent,
    canActivate: [AuthGuard],
    children: [
      {
        path: 'purchase-signup',
        data: { title: 'Purchase Account Setup', breadcrumb: 'Purchase Account Setup' },
        loadChildren: () => import('@modules/purchase-signup/purchase-signup.module').then(m => m.PurchaseSignupModule)
      },
      {
        path: '',
        data: { title: 'Dashboard', breadcrumb: 'Dashboard' },
        loadChildren: () => import('@modules/dashboard/dashboard.module').then(m => m.DashboardModule),
        canActivate: [SecondaryOwnerProhibitedGuard],
      },
      {
        path: 'invoice',
        data: { title: 'Invoice', breadcrumb: 'Invoice List' },
        loadChildren: () => import('@modules/invoice/invoice.module').then(m => m.InvoiceModule)
      },
      // {
      //   path: 'kyc-details',
      //   canActivate: [LeadAdminGuard],
      //   data: { title: 'KYC-Details', breadcrumb: 'KYC Details' },
      //   loadChildren: () => import('@modules/kyc/kyc.module').then(m => m.KYCModule)
      // },
      {
        path: 'admin-account',
        canActivate: [LeadAdminGuard],
        data: { title: 'Admin Account', breadcrumb: 'Admin Account' },
        loadChildren: () => import('@modules/admin-account/admin-account.module').then(m => m.AdminAccountModule)
      },
      // {
      //   path: 'banking',
      //   canActivate: [OwnerGuard, SecondaryOwnerProhibitedGuard],
      //   data: { title: 'Banking', breadcrumb: 'Banking' },
      //   loadChildren: () => import('@modules/banking/banking.module').then(m => m.BankingModule)
      // },
      {
        path: 'tag',
        canActivate: [SecondaryOwnerProhibitedGuard, CommonSubscriptionGuard],
        data: { title: 'Tags', breadcrumb: 'Tags' },
        loadChildren: () => import('@modules/tag/tag.module').then(m => m.TagModule)
      },
      {
        path: 'tally-config',
        canActivate: [SecondaryOwnerProhibitedGuard],
        data: { title: 'Tally', breadcrumb: 'Invoice purchase Configuration' },
        loadChildren: () => import('@modules/tally/tally.module').then(m => m.TallyModule)
      },
      {
        path: 'payments',
        canActivate: [SecondaryOwnerProhibitedGuard],
        data: { title: 'Payments', breadcrumb: 'Payments List' },
        loadChildren: () => import('@modules/payments/payments.module').then(m => m.PaymentsModule)
      },
      {
        path: 'compare-invoice',
        canActivate: [AuthGuard],
        data: { title: 'Compare Invoices', breadcrumb: 'Compare Invoices' },
        loadChildren: () => import('@modules/compare-invoice/compare-invoice.module').then(m => m.CompareInvoiceModule)
      },
      {
        path: 'entity',
        canActivate: [OwnerGuard, SecondaryOwnerProhibitedGuard],
        data: { title: 'Entity', breadcrumb: 'Entity Management' },
        loadChildren: () => import('@modules/entity/entity.module').then(m => m.EntityModule)
      },
      {
        path: 'profile',
        canActivate: [OwnerEntityGuard],
        data: { title: 'Profile', breadcrumb: 'Profile' },
        loadChildren: () => import('@modules/profile/profile.module').then(m => m.ProfileModule)
      },
      {
        path: 'voucher',
        canActivate: [OwnerEntityGuard, SecondaryOwnerProhibitedGuard, CommonSubscriptionGuard],
        data: { title: 'Voucher', breadcrumb: 'Voucher' },
        loadChildren: () => import('@modules/voucher/voucher.module').then(m => m.VoucherModule)
      },
      {
        path: 'integrations',
        canActivate: [OwnerEntityGuard],
        data: { title: 'Integrations', breadcrumb: 'Integrations' },
        loadChildren: () => import('@modules/integrations/integrations.module').then(m => m.IntegrationsModule)
      },
      {
        path: 'petty-expense',
        canActivate: [OwnerEntityGuard, SecondaryOwnerProhibitedGuard],
        data: { title: 'Petty Expense', breadcrumb: 'Petty Expense' },
        loadChildren: () => import('@modules/petty-expense/petty-expense.module').then(m => m.PettyExpenseModule)
      },
      {
        path: 'push-to-petpooja',
        canActivate: [SecondaryOwnerProhibitedGuard, CommonSubscriptionGuard],
        data: { title: 'Push To Petpooja', breadcrumb: 'Push To Petpooja' },
        loadChildren: () => import('@modules/push-to-petpooja/push-to-petpooja.module').then(m => m.PushToPetpoojaModule)
      },
      {
        path: 'invoice-review-summary',
        canActivate: [LeadAdminGuard],
        data: { title: 'Invoice Review Summary', breadcrumb: 'Invoice Review Summary' },
        loadChildren: () => import('@modules/invoice-review-summary/invoice-review-summary.module').then(m => m.InvoiceReviewSummaryModule)
      },
      {
        path: 'report',
        canActivate: [SecondaryOwnerProhibitedGuard, CanViewReportGuard],
        data: { title: 'Reports', breadcrumb: 'Reports' },
        loadChildren: () => import('@modules/report/report.module').then(m => m.ReportsModule)
      },
      {
        path: 'buyer',
        canActivate: [LeadAdminGuard],
        data: { title: 'Buyer', breadcrumb: 'Buyer' },
        loadChildren: () => import('@modules/buyer/buyer.module').then(m => m.BuyerModule)
      },
      {
        path: 'hyperpure-reports',
        canActivate: [LeadAdminGuard],
        data: { title: 'Hyperpure Reports', breadcrumb: 'Hyperpure Reports' },
        loadChildren: () => import('@modules/hyperpure-reports/hyperpure-reports.module').then(m => m.HyperpureReportsModule)
      },
      {
        path: 'deliverit-reports',
        canActivate: [LeadAdminGuard],
        data: { title: 'DeliverIt Reports', breadcrumb: 'DeliverIt Reports' },
        loadChildren: () => import('@modules/deliverit-reports/deliverit-reports.module').then(m => m.DeliveritReportsModule)
      },
      {
        path: 'deliverit-reports',
        canActivate: [LeadAdminGuard],
        data: { title: 'DeliverIt Reports', breadcrumb: 'DeliverIt Reports' },
        loadChildren: () => import('@modules/deliverit-reports/deliverit-reports.module').then(m => m.DeliveritReportsModule)
      },
      {
        path: 'utilities/invoice-reupload',
        canActivate: [LeadAdminGuard],
        data: { title: 'Invoice Re-upload', breadcrumb: 'Invoice Re-upload' },
        loadChildren: () => import('@modules/invoice-reupload/invoice-reupload.module').then(m => m.InvoiceReuploadModule)
      },

      {
        path: 'purchase-manager',
        canActivate: [AuthGuard, SecondaryOwnerProhibitedGuard, AdminProhibitedGuard],
        data: { title: 'Purchase Manager', breadcrumb: 'Purchase Manager' },
        loadChildren: () => import('@modules/purchase-manager/purchase-manager.module').then(m => m.PurchaseManagerModule)
      },
      {
        path: 'hyperpure',
        canActivate: [AuthGuard, SecondaryOwnerProhibitedGuard],
        data: { title: 'Price Discovery', breadcrumb: 'Price Discovery' },
        loadChildren: () => import('@modules/hyperpure/hyperpure.module').then(m => m.HyperpureModule)
      },
      {
        path: 'supplier',
        canActivate: [LeadAdminGuard],
        data: { title: 'Supplier', breadcrumb: 'Supplier' },
        loadChildren: () => import('@modules/supplier/supplier.module').then(m => m.SupplierModule)
      },
      {
        path: 'push-to-retail-pos',
        canActivate: [SecondaryOwnerProhibitedGuard, CommonSubscriptionGuard],
        data: { title: 'Push To Invoice', breadcrumb: 'Push To Invoice' },
        loadChildren: () => import('@modules/push-to-retail-pos/push-to-retail-pos.module').then(m => m.PushToRetailPosModule)
      },
      {
        path: 'push-to-tally',
        canActivate: [SecondaryOwnerProhibitedGuard, CommonSubscriptionGuard],
        data: { title: 'Push To Tally', breadcrumb: 'Push To Tally' },
        loadChildren: () => import('@modules/push-to-tally/push-to-tally.module').then(m => m.PushToTallyModule)
      },
      {
        path: 'user-management',
        canActivate: [OwnerGuard, SecondaryOwnerProhibitedGuard],
        data: { title: 'User Management', breadcrumb: 'User Management' },
        loadChildren: () => import('@modules/user-management/user-management.module').then(m => m.UserManagementModule)
      },
      {
        path: 'company-management',
        canActivate: [OwnerGuard, SecondaryOwnerProhibitedGuard],
        data: { title: 'Company Management', breadcrumb: 'Company Management' },
        loadChildren: () => import('@modules/company-management/company-management.module').then(m => m.CompanyManagementModule)
      },
      {
        path: 'lead-admin-user-management',
        canActivate: [LeadAdminGuard],
        data: { title: 'User Management', breadcrumb: 'User Management' },
        loadChildren: () => import('@modules/lead-admin-user-management/lead-admin-user-management.module').then(m => m.LeadAdminUserManagementModule)
      },
      {
        path: 'child-lead-admin-management',
        canActivate: [LeadAdminGuard],
        data: { title: 'Secondary Lead Admin', breadcrumb: 'Secondary Lead Admin' },
        loadChildren: () => import('@modules/child-lead-admin-management/child-lead-admin-management.module').then(m => m.ChildLeadAdminManagementModule)
      },
      {
        path: 'hyperpure-deactivation',
        canActivate: [LeadAdminGuard],
        data: { title: 'Hyperpure Deactivation', breadcrumb: 'Hyperpure Deactivation' },
        loadChildren: () => import('@modules/lead-admin-user-management/hyperpure-deactivation.module').then(m => m.HyperpureDeactivationModule)
      },
      {
        path: 'dormancy',
        canActivate: [LeadAdminGuard],
        data: { title: 'Dormancy', breadcrumb: 'Dormancy' },
        loadChildren: () => import('@modules/dormancy/dormancy.module').then(m => m.DormancyModule)
      },
      // {
      //   path: '',
      //   canActivate: [LeadAdminGuard],
      //   data: { title: 'Account Dashboard', breadcrumb: 'Account Dashboard' },
      //   loadChildren: () => import('@modules/account-dashboard/account-dashboard.module').then(m => m.AccountDashboardModule)
      // },
      // {
      //   path: 'billing',
      //   canActivate: [OwnerGuard, SecondaryOwnerProhibitedGuard],
      //   data: { title: 'Billing & Payments', breadcrumb: 'Billing & Payments' },
      //   loadChildren: () => import('@modules/billing/billing.module').then(m => m.BillingModule)
      // },
      // {
      //   path: '',
      //   component: EmptyLayoutComponent,
      //   data: { title: 'Invoice Image', breadcrumb: 'Invoice' },
      //   loadChildren: () => import('@modules/invoice/invoice.module').then(m => m.InvoiceModule)
      // },
      {
        path: 'invoice-extraction',
        data: { title: 'AI Assistant', breadcrumb: 'AI Assistant' },
        component: InvoiceExtractionComponent
      },
    ]
  },
  {
    path: '',
    component: BlankLayoutComponent,
    children: [
      {
        path: '',
        data: { title: 'Terms', breadcrumb: 'terms' },
        loadChildren: () => import('@modules/terms/terms.module').then(m => m.TermsModule)
      },
      {
        path: '',
        data: { title: 'Refund Policy', breadcrumb: 'Refund Policy' },
        loadChildren: () => import('@modules/refund-policy/refund-policy.module').then(m => m.RefundPolicyModule)
      },
      {
        path: '',
        data: { title: 'Privacy', breadcrumb: 'privacy' },
        loadChildren: () => import('@modules/privacy/privacy.module').then(m => m.PrivacyModule)
      },
    ]
  },
  {
    path: '**',
    data: { title: 'Not Found' },
    component: NotFoundComponent
  },
  {
    path: '404',
    data: { title: 'Not Found' },
    component: NotFoundComponent
  }
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { useHash: false, preloadingStrategy: QuicklinkStrategy, relativeLinkResolution: 'legacy' })],
  exports: [RouterModule]
})
export class AppRoutingModule { }